import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';

import {
  fetchCollectionAllocationAdjustments,
  fetchCollectionAllocations,
} from '#/api/crmMgmt/customerOrder/collection';
import { PERM } from '#/config/permissions';
import { Button } from '#/modules/commonComponents/button';
import {
  allocationEffectiveAmount,
  canRecordCollection,
} from '#/modules/crmMgmt/customerOrder/helper/collection';
import { useCan } from '#/modules/infra/auth/useCan';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

import {
  CollectionAllocationAdjustDialog,
  CollectionAllocationCreateDialog,
} from './CollectionAllocationDialogs';
import CollectionSimpleTable from './CollectionSimpleTable';
import { collectionQueryKey } from './collectionQueryKeys';

const CollectionAllocationsCard = ({ detail, receipts }) => {
  const customerOrderId = detail.id;
  const salesOrders = useMemo(() => detail.salesOrders ?? [], [detail.salesOrders]);
  const canUpdate = useCan(PERM.FMS_CUSTOMER_COLLECTION_UPDATE);
  const editable = canUpdate && canRecordCollection(detail.status);
  const [dialog, setDialog] = useState(null);

  const allocationsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'allocations'),
    queryFn: () => fetchCollectionAllocations(customerOrderId),
  });
  const adjustmentsQuery = useQuery({
    queryKey: collectionQueryKey(customerOrderId, 'allocationAdjustments'),
    queryFn: () => fetchCollectionAllocationAdjustments(customerOrderId),
  });
  const allocations = useMemo(() => allocationsQuery.data?.data ?? [], [allocationsQuery.data]);
  const adjustments = adjustmentsQuery.data?.data ?? [];

  const columns = useMemo(() => {
    const soNoById = new Map(salesOrders.map((so) => [so.id, so.salesOrderNo]));
    const receiptById = new Map(receipts.map((receipt) => [receipt.id, receipt]));
    const soLabel = (id) => (id ? (soNoById.get(id) ?? id.slice(0, 8)) : '（釋放）');
    return {
      soLabel,
      allocation: [
        {
          id: 'salesOrder',
          header: '銷貨單',
          accessorFn: (row) => row.salesOrderNo ?? soLabel(row.salesOrderId),
          size: 140,
          enableSorting: false,
        },
        {
          id: 'receipt',
          header: '來源實收',
          accessorFn: (row) => {
            const receipt = receiptById.get(row.receiptId);
            return receipt
              ? `${formatDateTimeOrDash(receipt.actualReceivedAt)}・${formatDisplayAmount(receipt.amount)}`
              : '—';
          },
          size: 200,
          enableSorting: false,
        },
        {
          id: 'amount',
          header: '分配額',
          accessorFn: (row) => formatDisplayAmount(row.amount),
          size: 110,
          enableSorting: false,
          meta: { cellClassName: 'td_right' },
        },
        {
          id: 'effectiveAmount',
          header: '目前有效額',
          accessorFn: (row) => formatDisplayAmount(allocationEffectiveAmount(row)),
          size: 110,
          enableSorting: false,
          meta: { cellClassName: 'td_right' },
        },
        {
          id: 'actions',
          header: '操作',
          size: 90,
          enableSorting: false,
          cell: ({ row }) => (editable && allocationEffectiveAmount(row.original) > 0 ? (
            <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'adjust', allocation: row.original })}>
              移轉
            </Button>
          ) : null),
        },
      ],
      adjustment: [
        {
          id: 'createdAt',
          header: '時間',
          accessorFn: (row) => formatDateTimeOrDash(row.createdAt ?? row.adjustedAt),
          size: 150,
          enableSorting: false,
        },
        {
          id: 'from',
          header: '從',
          accessorFn: (row) => soLabel(row.fromSalesOrderId),
          size: 120,
          enableSorting: false,
        },
        {
          id: 'to',
          header: '到',
          accessorFn: (row) => soLabel(row.toSalesOrderId),
          size: 120,
          enableSorting: false,
        },
        {
          id: 'amount',
          header: '金額',
          accessorFn: (row) => formatDisplayAmount(row.amount),
          size: 100,
          enableSorting: false,
          meta: { cellClassName: 'td_right' },
        },
        {
          id: 'reason',
          header: '原因',
          accessorFn: (row) => row.reason || '—',
          size: 180,
          enableSorting: false,
          meta: { cellClassName: 'td_left' },
        },
      ],
    };
  }, [salesOrders, receipts, editable]);

  const closeDialog = (nextOpen) => {
    if (!nextOpen) {
      setDialog(null);
    }
  };

  return (
    <div className="space-y-2">
      {editable ? (
        <div className="flex justify-end px-4 pt-3">
          <Button type="button" size="sm" onClick={() => setDialog({ type: 'create' })}>
            分配到銷貨單
          </Button>
        </div>
      ) : null}
      <CollectionSimpleTable
        rows={allocations}
        columns={columns.allocation}
        emptyText="尚無子單分配"
        rowIdPrefix="allocation"
      />
      {adjustments.length > 0 ? (
        <>
          <p className="px-4 pt-2 text-sm text-slate-600">分配調整紀錄</p>
          <CollectionSimpleTable
            rows={adjustments}
            columns={columns.adjustment}
            emptyText="尚無調整紀錄"
            rowIdPrefix="adjustment"
          />
        </>
      ) : null}

      {dialog?.type === 'create' ? (
        <CollectionAllocationCreateDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          receipts={receipts}
          salesOrders={salesOrders}
        />
      ) : null}
      {dialog?.type === 'adjust' ? (
        <CollectionAllocationAdjustDialog
          open
          onOpenChange={closeDialog}
          customerOrderId={customerOrderId}
          allocation={dialog.allocation}
          allocations={allocations}
          salesOrders={salesOrders}
        />
      ) : null}
    </div>
  );
};

CollectionAllocationsCard.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
    salesOrders: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  receipts: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CollectionAllocationsCard;
